/**
 * AudioTourDetail.jsx
 *
 * Full-page detail view for a single audio tour. Reached via:
 *   /audio-tours/:tourId
 *
 * Purpose:
 *   - SEO-friendly landing pages for each tour (indexable content per tour)
 *   - Shareable link when a customer wants to send a specific tour to a friend
 *   - Deep-link target from the AI Concierge upsell tiles
 *
 * Renders:
 *   - Hero image with title + winery + duration
 *   - Buy CTA ($X.XX) OR Play button (if already purchased)
 *   - Preview button (if a preview exists AND not yet purchased)
 *   - Full description (safely rendered — WordPress content may include HTML)
 *   - Highlights / "What you'll learn"
 *   - "Related tours" block (up to 3 other tours from the same winery/region)
 */
import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Play, Pause, ArrowLeft, Clock, Star, Headphones,
  CheckCircle, ShoppingCart, ShareNetwork,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';
import { getGuestTourIds } from '@/lib/guestPurchases';
import api from '@/lib/api';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

export default function AudioTourDetail() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const { playTrack, currentTrack, isPlaying } = useAudioPlayer();

  const [tour, setTour] = useState(null);
  const [allTours, setAllTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasedIds, setPurchasedIds] = useState([]);

  // Hydrate purchases (guest localStorage + logged-in customer)
  useEffect(() => {
    const guestIds = getGuestTourIds();
    setPurchasedIds(guestIds);
    const customerToken = localStorage.getItem('customer_token');
    if (customerToken) {
      api.get('/audio-tours/purchased', { headers: { Authorization: `Bearer ${customerToken}` } })
        .then((r) => {
          const ids = (r.data || []).map((t) => t.tour_id);
          setPurchasedIds((prev) => Array.from(new Set([...prev, ...ids])));
        })
        .catch(() => {});
    }
  }, []);

  // Load the tour + full list (for "related tours" section)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/audio-tours')
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setAllTours(list);
        const match = list.find((t) => t.tour_id === tourId);
        if (!match) setError('Tour not found');
        else setTour(match);
      })
      .catch(() => !cancelled && setError('Could not load tour'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [tourId]);

  const isPurchased = tour && purchasedIds.includes(tour.tour_id);
  const isCurrentlyPlaying = tour && currentTrack?.tour_id === tour.tour_id && isPlaying;

  // Pick up to 3 related tours: same winery first, then anything else
  const related = useMemo(() => {
    if (!tour || allTours.length < 2) return [];
    const others = allTours.filter((t) => t.tour_id !== tour.tour_id);
    const sameWinery = others.filter((t) => t.winery_name === tour.winery_name);
    const different = others.filter((t) => t.winery_name !== tour.winery_name);
    return [...sameWinery, ...different].slice(0, 3);
  }, [tour, allTours]);

  const handlePlayFull = () => {
    if (!tour) return;
    playTrack({ ...tour, is_preview: false }, true);
  };
  const handlePreview = () => {
    if (!tour) return;
    playTrack({ ...tour, is_preview: true });
  };
  const handleBuy = () => tour && navigate(`/checkout/${tour.tour_id}`);
  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: tour.title, text: `Check out this audio tour: ${tour.title}`, url }); }
      catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f8f5f0] to-white flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#722F37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !tour) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f8f5f0] to-white flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="text-lg font-serif text-gray-900 mb-2">Tour not found</div>
          <p className="text-sm text-gray-600 mb-4">This audio tour may have been removed or the link is incorrect.</p>
          <Button onClick={() => navigate('/audio-tours')} variant="outline">
            <ArrowLeft size={16} className="mr-2" /> Browse all tours
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f5f0] to-white">
      <SEO
        title={`${tour.title} · ${tour.winery_name} Audio Tour`}
        description={(tour.description || '').replace(/<[^>]*>/g, '').slice(0, 160)}
        image={tour.cover_image_url}
        path={`/audio-tours/${tourId}`}
        type="product"
        product={{
          name: tour.title,
          description: (tour.description || '').replace(/<[^>]*>/g, '').slice(0, 300),
          image: tour.cover_image_url,
          priceCents: Math.round(parseFloat(tour.price || 0) * 100),
        }}
        breadcrumbs={[
          { name: 'Audio Tours', path: '/audio-tours' },
          { name: tour.title, path: `/audio-tours/${tourId}` },
        ]}
      />
      {/* AudioObject + additional Product schema for GEO/Perplexity */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AudioObject',
          name: tour.title,
          description: (tour.description || '').replace(/<[^>]*>/g, '').slice(0, 300),
          thumbnailUrl: tour.cover_image_url,
          duration: tour.duration_minutes ? `PT${tour.duration_minutes}M` : undefined,
          encodingFormat: 'audio/mpeg',
          inLanguage: 'en',
          provider: {
            '@type': 'Organization',
            name: 'Winery Finder',
            url: 'https://www.wineryfinderapp.com',
          },
          offers: {
            '@type': 'Offer',
            price: parseFloat(tour.price || 0).toFixed(2),
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url: `https://www.wineryfinderapp.com/audio-tours/${tourId}`,
          },
          about: {
            '@type': 'TouristDestination',
            name: tour.region || tour.winery_name || 'Wine Country',
          },
        })}</script>
      </Helmet>

      <div className="container mx-auto px-4 max-w-5xl py-6">
        <Button variant="ghost" onClick={() => navigate('/audio-tours')} className="mb-4 text-gray-600 hover:text-[#722F37]" data-testid="detail-back-btn">
          <ArrowLeft size={16} className="mr-2" /> All audio tours
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Hero image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="md:col-span-3 rounded-2xl overflow-hidden shadow-lg relative aspect-[4/3] md:aspect-[16/10]"
            data-testid="detail-hero"
          >
            {tour.cover_image_url ? (
              <img src={tour.cover_image_url} alt={tour.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-wine-800 to-wine-950 flex items-center justify-center">
                <Headphones size={64} className="text-white/40" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2">
              {tour.duration_minutes && (
                <span className="inline-flex items-center gap-1 bg-white/90 text-[#722F37] px-3 py-1 rounded-full text-xs font-medium">
                  <Clock size={14} /> {tour.duration_minutes} min
                </span>
              )}
              {tour.rating && (
                <span className="inline-flex items-center gap-1 bg-white/90 text-amber-700 px-3 py-1 rounded-full text-xs font-medium">
                  <Star size={14} weight="fill" /> {tour.rating}
                </span>
              )}
              {isPurchased && (
                <span className="inline-flex items-center gap-1 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  <CheckCircle size={14} weight="fill" /> Purchased
                </span>
              )}
            </div>
          </motion.div>

          {/* Meta + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="md:col-span-2 flex flex-col gap-3"
          >
            <div>
              <h1 className="font-serif text-2xl md:text-3xl text-gray-900 leading-tight mb-1" data-testid="detail-title">
                {tour.title}
              </h1>
              <p className="text-[#722F37] font-medium">{tour.winery_name}</p>
            </div>

            {isPurchased ? (
              <Button
                onClick={handlePlayFull} size="lg"
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="detail-play-btn"
              >
                {isCurrentlyPlaying ? <Pause size={20} weight="fill" className="mr-2" /> : <Play size={20} weight="fill" className="mr-2" />}
                {isCurrentlyPlaying ? 'Playing…' : 'Play this tour'}
              </Button>
            ) : (
              <>
                <div className="text-3xl font-bold text-[#722F37]">
                  ${tour.price ? Number(tour.price).toFixed(2) : '0.00'}
                  <span className="text-sm text-gray-500 font-normal ml-2">one-time purchase</span>
                </div>
                <Button
                  onClick={handleBuy} size="lg"
                  className="bg-[#722F37] hover:bg-[#5d2530] text-white"
                  data-testid="detail-buy-btn"
                >
                  <ShoppingCart size={18} className="mr-2" /> Buy audio tour
                </Button>
                {tour.audio_url && (
                  <Button
                    onClick={handlePreview} variant="outline"
                    data-testid="detail-preview-btn"
                  >
                    <Play size={16} weight="fill" className="mr-2" /> Preview 30 sec
                  </Button>
                )}
              </>
            )}

            <Button variant="ghost" onClick={handleShare} className="text-gray-500" data-testid="detail-share-btn">
              <ShareNetwork size={16} className="mr-2" /> Share this tour
            </Button>
          </motion.div>
        </div>

        {/* Description + Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="md:col-span-2 bg-white rounded-2xl shadow p-6"
            data-testid="detail-description"
          >
            <h2 className="font-serif text-xl text-gray-900 mb-3">About this tour</h2>
            {tour.description ? (
              <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                   dangerouslySetInnerHTML={{ __html: tour.description }} />
            ) : (
              <p className="text-sm text-gray-500 italic">No description provided.</p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow p-6 h-fit"
            data-testid="detail-highlights"
          >
            <h2 className="font-serif text-xl text-gray-900 mb-3 flex items-center gap-2">
              <Headphones size={18} className="text-[#722F37]" /> What you&apos;ll learn
            </h2>
            {tour.highlights?.length > 0 ? (
              <ul className="space-y-2">
                {tour.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle size={16} weight="fill" className="text-[#722F37] mt-0.5 flex-shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 italic">Coming soon.</p>
            )}
          </motion.div>
        </div>

        {/* Related tours */}
        {related.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="mt-10"
            data-testid="detail-related"
          >
            <h2 className="font-serif text-xl text-gray-900 mb-4">More audio tours you might like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.tour_id} to={`/audio-tours/${r.tour_id}`}
                  className="group block bg-white rounded-xl shadow hover:shadow-lg transition-all overflow-hidden"
                  data-testid={`detail-related-${r.tour_id}`}
                >
                  {r.cover_image_url && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={r.cover_image_url} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                    </div>
                  )}
                  <div className="p-3">
                    <div className="font-medium text-sm text-gray-900 line-clamp-2">{r.title}</div>
                    <div className="text-xs text-[#722F37] mt-1">{r.winery_name}</div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                      <span>{r.duration_minutes} min</span>
                      <span className="font-semibold text-[#722F37]">${Number(r.price || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
